import React, { Fragment, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
    Button,
    FormCheck,
    FormControl,
    FormGroup,
    FormLabel,
    Row,
    Col,
    Card,
} from "react-bootstrap";
import Api from "src/data/api";
import { v4 as uuidv4 } from "uuid";
import styles from "./SettingsQueues.module.scss";
import { useDispatch } from "react-redux";
import { showNotification } from "src/redux/notificationActions";
import { ERROR_MESSAGES, QUEUES_MESSAGES } from "src/constants/messages";
import { NOTIFICATION_TYPES } from "src/constants/notificationTypes";
import { NOTIFICATION_HEADERS } from "src/constants/notificationHeaders";

// TODO:
// handle errors
const fetchQueues = async () => {
    let { data } = await Api.request({
        url: `/retailer/queues`,
    });

    return data;
};

const fetchRetailerAdditionalSettings = async () => {
    let { data } = await Api.request({
        url: "/retailer/settings/additional-settings",
    });
    return data;
};

const saveStaticAppointmentQueueEnabled = async (
    staticAppointmentQueueEnabled
) => {
    await Api.request({
        method: "POST",
        url: "/retailer/settings/additional-settings",
        data: {
            static_appointment_queue_enabled: staticAppointmentQueueEnabled,
        },
    });
};

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
    // styles we need to apply on draggables
    ...draggableStyle,
});

const checkboxFields = [
    "send_message_on_queue_enter",
    "send_message_on_next_in_line",
    "is_cart_mandatory",
];

const getEnterQueueDefaultMessage = (queue_name) =>
    `You have now entered the ${queue_name} queue. We will text you when you are next in line!`;

const getNextInLineDefaultMessage = (queue_name) =>
    `You are next in line on the ${queue_name} queue. Please return to the store in the next 10 minutes!`;

const SettingsQueues = () => {
    const [queues, setQueues] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [highestPosition, setHighestPosition] = useState(1);
    const [staticAppointmentQueueEnabled, setStaticAppointmentQueueEnabled] =
        useState(false);
    const [selectedQueue, setSelectedQueue] = useState(null);
    const dispatch = useDispatch();

    const addNewQueue = () => {
        const newQueue = {
            queue_id: uuidv4(),
            name: "",
            position: highestPosition + 1,
            isNew: true,
            send_message_on_queue_enter: false,
            message_on_queue_enter: "",
            send_message_on_next_in_line: false,
            message_on_next_in_line: "",
            is_cart_mandatory: false,
        };
        setSelectedQueue(newQueue);
    };

    const createQueue = async () => {
        setIsSubmitting(true);
        const { isNew, ...newQueueData } = selectedQueue;

        const newQueue = {
            ...newQueueData,
            message_on_queue_enter:
                selectedQueue.message_on_queue_enter.trim() ||
                getEnterQueueDefaultMessage(selectedQueue.name),
            message_on_next_in_line:
                selectedQueue.message_on_next_in_line.trim() ||
                getNextInLineDefaultMessage(selectedQueue.name),
        };

        try {
            await Api.request({
                method: "POST",
                url: `/retailer/queues`,
                data: newQueue,
            });

            const newQueues = [...queues, newQueue];

            setQueues(newQueues);
            setSelectedQueue(null);
            setHighestPosition((prev) => prev + 1);
            dispatch(
                showNotification(
                    QUEUES_MESSAGES.QUEUE_CREATE_SUCCESS,
                    NOTIFICATION_TYPES.SUCCESS,
                    NOTIFICATION_HEADERS.QUEUES_SETTINGS
                )
            );
        } catch (error) {
            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const startQueueEdit = () => {
        setSelectedQueue({
            ...selectedQueue,
            isEdit: true,
            previous: { ...selectedQueue },
        });
    };

    const cancelQueueEdit = () => {
        setSelectedQueue({ ...selectedQueue.previous });
    };

    const editQueue = async () => {
        setIsSubmitting(true);
        if (!selectedQueue) {
            return;
        }
        const { previous, isEdit, ...data } = selectedQueue;

        data.message_on_queue_enter =
            data.message_on_queue_enter.trim() ||
            getEnterQueueDefaultMessage(data.name);

        data.message_on_next_in_line =
            data.message_on_next_in_line.trim() ||
            getNextInLineDefaultMessage(data.name);

        try {
            await Api.request({
                method: "PUT",
                url: `/retailer/queues/${data.queue_id}`,
                data,
            });

            const newQueues = queues.map((q) => {
                if (q.queue_id === data.queue_id) {
                    return data;
                }
                return q;
            });

            setQueues(newQueues);
            setSelectedQueue(data);
            dispatch(
                showNotification(
                    QUEUES_MESSAGES.QUEUE_UPDATE_SUCCESS,
                    NOTIFICATION_TYPES.SUCCESS,
                    NOTIFICATION_HEADERS.QUEUES_SETTINGS
                )
            );
        } catch (error) {
            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const deleteQueue = async () => {
        setIsSubmitting(true);
        if (!selectedQueue) {
            return;
        }

        try {
            await Api.request({
                method: "DELETE",
                url: `/retailer/queues/${selectedQueue.queue_id}`,
            });

            const newQueues = queues.filter(
                (q) => q.queue_id !== selectedQueue.queue_id
            );

            setQueues(newQueues);
            setSelectedQueue(null);
            setHighestPosition(newQueues[newQueues.length - 1]?.position || 1);
            dispatch(
                showNotification(
                    QUEUES_MESSAGES.QUEUE_DELETE_SUCCESS,
                    NOTIFICATION_TYPES.SUCCESS,
                    NOTIFICATION_HEADERS.QUEUES_SETTINGS
                )
            );
        } catch (error) {
            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        fetchQueues().then((data) => {
            setQueues(data.retailerQueues);
            setHighestPosition(
                data.retailerQueues[data.retailerQueues.length - 1]?.position ||
                    1
            );
        });

        fetchRetailerAdditionalSettings().then((data) => {
            setStaticAppointmentQueueEnabled(
                data.retailerAdditionalSettings.static_appointment_queue_enabled
            );
        });
    }, []);

    const onDragEnd = (result) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        const items = reorder(
            queues,
            result.source.index,
            result.destination.index
        );

        setQueues(items);
    };

    const handleSaveQueueOrder = async () => {
        setIsSubmitting(true);
        try {
            const { data } = await Api.request({
                method: "POST",
                url: `/retailer/queues/order`,
                data: {
                    queues,
                },
            });
            setQueues(data.queues);
            dispatch(
                showNotification(
                    QUEUES_MESSAGES.QUEUE_REORDER_SUCCESS,
                    NOTIFICATION_TYPES.SUCCESS,
                    NOTIFICATION_HEADERS.QUEUES_SETTINGS
                )
            );
        } catch (error) {
            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const getHandleChange = (fieldName) => {
        const handleChange = (e) => {
            const value = checkboxFields.includes(fieldName)
                ? !selectedQueue[fieldName]
                : e.target.value;

            setSelectedQueue({ ...selectedQueue, [fieldName]: value });
        };

        return handleChange;
    };

    return (
        <div className={styles.queue}>
            <Row>
                <Col>
                    <Row style={{ paddingTop: "1rem" }}>
                        <Col>
                            <h1>Queues</h1>
                            <FormCheck
                                custom
                                type="checkbox"
                                id="static-appointment-queue_enabled"
                                label="Enable appointments in queues"
                                checked={staticAppointmentQueueEnabled}
                                name={`staticAppointmentQueueEnabled`}
                                onChange={() => {
                                    setStaticAppointmentQueueEnabled(
                                        (prev) => !prev
                                    );
                                    saveStaticAppointmentQueueEnabled(
                                        !staticAppointmentQueueEnabled
                                    );
                                }}
                            />
                        </Col>
                        <Col>
                            <Button onClick={addNewQueue}>Add new queue</Button>
                        </Col>
                    </Row>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable">
                            {(provided) => (
                                <div
                                    className={styles.queueList}
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {queues.map((queue, index) => (
                                        <Draggable
                                            key={queue.queue_id}
                                            draggableId={queue.queue_id}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps
                                                            .style
                                                    )}
                                                    onClick={() => {
                                                        setSelectedQueue(queue);
                                                    }}
                                                >
                                                    <Card
                                                        style={{
                                                            margin: "10px 0",
                                                        }}
                                                    >
                                                        <Card.Header>
                                                            {queue.name}
                                                        </Card.Header>
                                                    </Card>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Col>

                <Col>
                    {selectedQueue && (
                        <Fragment>
                            <FormGroup
                                controlId={`${selectedQueue.queue_id}-name`}
                            >
                                {!selectedQueue.isEdit &&
                                !selectedQueue.isNew ? (
                                    <h1>{selectedQueue.name}</h1>
                                ) : (
                                    <Fragment>
                                        <FormLabel>Queue name</FormLabel>
                                        <FormControl
                                            required
                                            type="text"
                                            name="name"
                                            value={selectedQueue.name}
                                            onChange={getHandleChange("name")}
                                        />
                                    </Fragment>
                                )}
                            </FormGroup>
                            <FormGroup
                                controlId={`${selectedQueue.queue_id}-isCartMandatory`}
                            >
                                <FormCheck
                                    disabled={
                                        !selectedQueue.isEdit &&
                                        !selectedQueue.isNew
                                    }
                                    custom
                                    type="checkbox"
                                    label="Cart is required when creating appointment for following queue"
                                    value={selectedQueue.is_cart_mandatory}
                                    checked={selectedQueue.is_cart_mandatory}
                                    name={`isCartMandatory`}
                                    onChange={getHandleChange(
                                        "is_cart_mandatory"
                                    )}
                                />
                            </FormGroup>
                            <FormGroup
                                controlId={`${selectedQueue.queue_id}-sendMessageOnNextInLine`}
                            >
                                <FormCheck
                                    disabled={
                                        !selectedQueue.isEdit &&
                                        !selectedQueue.isNew
                                    }
                                    custom
                                    type="checkbox"
                                    label="Send a message on the next in line appointment"
                                    value={
                                        selectedQueue.send_message_on_next_in_line
                                    }
                                    checked={
                                        selectedQueue.send_message_on_next_in_line
                                    }
                                    name={`sendMessageOnNextInLine`}
                                    onChange={getHandleChange(
                                        "send_message_on_next_in_line"
                                    )}
                                />
                            </FormGroup>
                            {selectedQueue.send_message_on_next_in_line && (
                                <FormGroup
                                    controlId={`${selectedQueue.queue_id}-nextInLineMessage`}
                                >
                                    <FormLabel>Next in line message</FormLabel>
                                    <FormControl
                                        disabled={
                                            !selectedQueue.isEdit &&
                                            !selectedQueue.isNew
                                        }
                                        required
                                        type="text"
                                        name={`messageOnNextInLine`}
                                        value={
                                            selectedQueue.message_on_next_in_line
                                        }
                                        onChange={getHandleChange(
                                            "message_on_next_in_line"
                                        )}
                                    />
                                </FormGroup>
                            )}
                            <FormGroup
                                controlId={`${selectedQueue.queue_id}-sendMessageOnQueueEnter`}
                            >
                                <FormCheck
                                    disabled={
                                        !selectedQueue.isEdit &&
                                        !selectedQueue.isNew
                                    }
                                    custom
                                    type="checkbox"
                                    label="Send message on appointment entering the queue"
                                    value={
                                        selectedQueue.send_message_on_queue_enter
                                    }
                                    checked={
                                        selectedQueue.send_message_on_queue_enter
                                    }
                                    name={`sendMessageOnQueueEnter`}
                                    onChange={getHandleChange(
                                        "send_message_on_queue_enter"
                                    )}
                                />
                            </FormGroup>
                            {selectedQueue.send_message_on_queue_enter && (
                                <FormGroup
                                    controlId={`${selectedQueue.queue_id}-messageOnQueueEnter`}
                                >
                                    <FormLabel>Enter queue message</FormLabel>
                                    <FormControl
                                        disabled={
                                            !selectedQueue.isEdit &&
                                            !selectedQueue.isNew
                                        }
                                        required
                                        type="text"
                                        name={`messageOnQueueEnter`}
                                        value={
                                            selectedQueue.message_on_queue_enter
                                        }
                                        onChange={getHandleChange(
                                            "message_on_queue_enter"
                                        )}
                                    />
                                </FormGroup>
                            )}
                            {selectedQueue.isNew && (
                                <Fragment>
                                    <Button
                                        name="Create-Queue"
                                        disabled={isSubmitting}
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={createQueue}
                                    >
                                        <i className="bi bi-check2"></i>
                                    </Button>
                                    <Button
                                        name="Cancel-Queue-Creation"
                                        disabled={isSubmitting}
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => setSelectedQueue(null)}
                                    >
                                        <i className="bi bi-x-lg"></i>
                                    </Button>
                                </Fragment>
                            )}
                            {selectedQueue.isEdit && (
                                <Fragment>
                                    <Button
                                        name="Edit-Queue"
                                        disabled={isSubmitting}
                                        size="sm"
                                        variant="outline-success"
                                        onClick={editQueue}
                                    >
                                        <i className="bi bi-check2"></i>
                                    </Button>
                                    <Button
                                        name="Cancel-Edit"
                                        disabled={isSubmitting}
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={cancelQueueEdit}
                                    >
                                        <i className="bi bi-x-lg"></i>
                                    </Button>
                                </Fragment>
                            )}
                            {!selectedQueue.isNew && !selectedQueue.isEdit && (
                                <Fragment>
                                    <Button
                                        name="Start-Queue-Edit"
                                        disabled={isSubmitting}
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={startQueueEdit}
                                    >
                                        <i className="bi bi-pencil"></i>
                                    </Button>
                                    <Button
                                        name="Delete-Queue"
                                        disabled={isSubmitting}
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={deleteQueue}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </Button>
                                </Fragment>
                            )}
                        </Fragment>
                    )}
                </Col>
            </Row>

            <Row></Row>
            {queues.length > 0 && (
                <Button onClick={handleSaveQueueOrder}>Save new order</Button>
            )}
        </div>
    );
};

export default SettingsQueues;
